import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../utils/classNames';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { ChevUpDownIcon, CheckIcon, PlusIcon, SettingsIcon } from '../../icons';

// Ported from LeadKitchen v3 src/shell.jsx WorkspaceSwitcher; the v3 shape
// carried city/role/color as first-class fields on each workspace. The real
// API (/api/auth/me) only returns { id, slug, companyName } per site, so
// - display name comes from `companyName` (falling back to slug, then id)
// - initials are derived on the fly (first letter of each of the first two
//   words — "Smoke Test Kitchen" → "ST")
// - background color is a deterministic gradient keyed off the site id so the
//   chip stays visually stable between reloads and across tenants
// - the secondary line shows the slug instead of the v3 "город · роль" to
//   avoid inventing data we don't have.

// Regular users always have exactly one site — the dropdown has no peers
// to switch to, so we disable the toggle and suppress menu rendering. For
// superadmins with N sites (N ≥ 2) the behaviour is unchanged.

const GRADIENTS = [
  'linear-gradient(135deg, #6ddfe2, #01848a)',
  'linear-gradient(135deg, #fbbf24, #f97316)',
  'linear-gradient(135deg, #a78bfa, #6366f1)',
  'linear-gradient(135deg, #f87171, #b91c1c)',
  'linear-gradient(135deg, #34d399, #059669)',
  'linear-gradient(135deg, #60a5fa, #1d4ed8)',
];

function hashString(input) {
  let h = 0;
  const s = String(input || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function gradientFor(id) {
  return GRADIENTS[hashString(id) % GRADIENTS.length];
}

function initialsOf(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function displayNameOf(ws) {
  return ws?.companyName || ws?.slug || ws?.id || 'Мой кабинет';
}

// Focus/Escape/outside-click handling replicates v3 UX; keyboard-focusable
// (tabIndex=0) so :focus-visible from layout.css (.lk-ws:focus-visible) lights
// up correctly.
export function WorkspaceSwitcher() {
  const { workspaces, current, switchTo } = useWorkspace();
  const safeWorkspaces = useMemo(() => (Array.isArray(workspaces) ? workspaces : []), [workspaces]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Exactly one workspace = superadmins with a single tenant and every
  // regular user. Collapse to a static chip with no dropdown affordance.
  const hasMultiple = safeWorkspaces.length > 1;

  // Precompute deterministic visuals for every workspace so the dropdown
  // and the collapsed chip agree on colors/initials.
  const decorated = useMemo(
    () =>
      safeWorkspaces.map((w) => ({
        ...w,
        _name: displayNameOf(w),
        _init: initialsOf(displayNameOf(w)),
        _color: gradientFor(w.id),
      })),
    [safeWorkspaces],
  );

  const activeDecorated = useMemo(() => {
    if (!current) return null;
    return decorated.find((w) => w.id === current.id) || null;
  }, [decorated, current]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!hasMultiple) return;
    setOpen((o) => !o);
  };
  const onKeyDown = (e) => {
    if (!hasMultiple) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  // Empty fallback — shouldn't happen for authed users (the seed guarantees
  // at least one linked site), but guards /login and the brief /me-loading
  // window from a crash.
  if (!activeDecorated) {
    return (
      <div
        className="lk-ws"
        aria-disabled="true"
        style={{ opacity: 0.6, cursor: 'default' }}
      >
        <div className="lk-ws__ico" style={{ background: GRADIENTS[0] }}>—</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="lk-ws__name lk-truncate">Нет доступных кабинетов</div>
          <div className="lk-ws__role">—</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      tabIndex={hasMultiple ? 0 : -1}
      role="button"
      aria-haspopup={hasMultiple ? 'listbox' : undefined}
      aria-expanded={hasMultiple ? open : undefined}
      aria-disabled={hasMultiple ? undefined : 'true'}
      className={cn('lk-ws', open && 'lk-ws--open')}
      onClick={toggle}
      onKeyDown={onKeyDown}
      style={hasMultiple ? undefined : { cursor: 'default' }}
    >
      <div className="lk-ws__ico" style={{ background: activeDecorated._color }}>
        {activeDecorated._init}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="lk-ws__name lk-truncate">{activeDecorated._name}</div>
        <div className="lk-ws__role lk-truncate">
          {activeDecorated.slug || activeDecorated.id}
        </div>
      </div>
      {hasMultiple && <ChevUpDownIcon className="lk-ws__chev" size={14} />}

      {open && hasMultiple && (
        <div className="lk-ws-menu" role="listbox" onClick={(e) => e.stopPropagation()}>
          {decorated.map((w) => (
            <div
              key={w.id}
              role="option"
              aria-selected={w.id === activeDecorated.id}
              className={cn(
                'lk-ws-menu__item',
                w.id === activeDecorated.id && 'lk-ws-menu__item--active',
              )}
              onClick={() => {
                switchTo(w.id);
                setOpen(false);
              }}
            >
              <div
                className="lk-ws__ico"
                style={{ background: w._color, width: 24, height: 24, fontSize: 10 }}
              >
                {w._init}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="lk-truncate" style={{ fontSize: 13 }}>
                  {w._name}
                </div>
                <div className="lk-ws__role lk-truncate">{w.slug || w.id}</div>
              </div>
              {w.id === activeDecorated.id && <CheckIcon size={14} />}
            </div>
          ))}
          <div className="lk-ws-menu__sep" />
          <div className="lk-ws-menu__item lk-ws-menu__action">
            <div
              style={{
                width: 24,
                height: 24,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 6,
                background: 'rgba(255,255,255,0.05)',
              }}
            >
              <PlusIcon size={12} />
            </div>
            Создать кабинет
          </div>
          <div className="lk-ws-menu__item lk-ws-menu__action">
            <div
              style={{
                width: 24,
                height: 24,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 6,
                background: 'rgba(255,255,255,0.05)',
              }}
            >
              <SettingsIcon size={12} />
            </div>
            Управление кабинетами
          </div>
        </div>
      )}
    </div>
  );
}
